import React, { useEffect, useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { SectionHeader, StatTile, Button, Loading, EmptyState, Tag } from "@/components/brand";
import { formatR } from "@/lib/format";
import dayjs from "dayjs";

export default function AdminOverview() {
  const qc = useQueryClient();
  const o = useQuery({ queryKey: ["admin", "overview"], queryFn: () => api("/admin/overview") });
  const payouts = useQuery({ queryKey: ["admin", "payouts"], queryFn: () => api("/admin/payouts") });
  const [rate, setRate] = useState("");
  useEffect(() => { if (o.data && rate === "") setRate(String(Math.round(o.data.commission_rate * 100))); }, [o.data, rate]);

  const saveRate = useMutation({ mutationFn: () => api("/admin/settings", { method: "PUT", body: { commission_rate: Number(rate) / 100 } }), onSuccess: () => { qc.invalidateQueries({ queryKey: ["admin", "overview"] }); toast.success("Commission updated"); }, onError: (e) => toast.error(e.message) });
  const markPaid = useMutation({ mutationFn: (pid) => api(`/admin/payouts/${pid}/mark-paid`, { method: "POST" }), onSuccess: () => { qc.invalidateQueries({ queryKey: ["admin", "payouts"] }); toast.success("Marked as paid"); } });

  if (o.isLoading) return <Loading />;
  const d = o.data;

  return (
    <div data-testid="admin-overview-screen" className="space-y-6">
      <SectionHeader title="Platform Overview" subtitle="Everything happening on Sofit." />
      <div className="flex flex-wrap gap-3">
        <StatTile testID="stat-members" label="Members" value={d.users} />
        <StatTile testID="stat-trainers" label="Trainers" value={d.trainers} />
        <StatTile testID="stat-classes" label="Live classes" value={d.classes} />
        <StatTile testID="stat-bookings" label="Bookings" value={d.bookings} />
        <StatTile testID="stat-gross" label="Gross volume" value={formatR(d.gross_volume)} />
        <StatTile testID="stat-commission" label="Commission earned" value={formatR(d.commission_earned)} />
      </div>

      <div className="p-4 rounded-xl2 border border-divider flex flex-wrap items-center gap-4">
        <div className="flex-1"><div className="font-display text-lg text-ink">Platform commission</div><div className="font-body text-sm text-mutedgrey">Percentage taken from each booking.</div></div>
        <div className="flex items-center gap-2">
          <input data-testid="commission-input" type="number" value={rate} onChange={(e) => setRate(e.target.value)} className="w-20 h-11 px-3 rounded-md border border-line bg-surface2 font-body text-center" />
          <span className="font-display text-ink">%</span>
          <Button testID="save-commission" title="Save" small loading={saveRate.isPending} onClick={() => saveRate.mutate()} />
        </div>
      </div>

      {d.pending_trainers > 0 ? <div className="p-4 rounded-xl2 bg-brand-soft font-body text-sm text-ink">{d.pending_trainers} trainer{d.pending_trainers > 1 ? "s" : ""} awaiting approval.</div> : null}

      <div>
        <h3 className="font-display text-xl text-ink mb-3">Payout Requests</h3>
        {(payouts.data ?? []).length === 0 ? <EmptyState title="No payout requests" /> : (
          <div className="space-y-2">
            {payouts.data.map((p) => (
              <div key={p.id} className="flex items-center gap-3 p-3 rounded-md border border-divider">
                <div className="flex-1"><div className="font-body font-semibold text-sm text-ink">{p.trainer_name} · {formatR(p.amount)}</div><div className="font-body text-xs text-mutedgrey">{dayjs(p.created_at).format("D MMM YYYY")}</div></div>
                {p.status === "paid" ? <Tag label="paid" tone="green" /> : <Button testID={`mark-paid-${p.id}`} title="Mark paid" small variant="dark" loading={markPaid.isPending} onClick={() => markPaid.mutate(p.id)} />}
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
